import React, { useState } from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { TextField, Select } from 'formik-material-ui';
import moment from 'moment';
import * as Yup from 'yup';
import codes from 'country-calling-code';
import { find } from 'lodash';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CloseIcon from '@material-ui/icons/Close';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import * as MemberService from '../../api/memberService';
// import * as StampCardService from '../../api/stampCardService';
import * as UserService from '../../api/userService';
import useCompanyContext from '../../hooks/useCompanyContext';
import DatePicker from '../FormikAdapter/DatePicker';
import AlertExistingMemberEmailDialog from './AlertExistingMemberEmailDialog';
import AlertExistingMemberMobileDialog from './AlertExistingMemberMobileDialog';
import AlertDifferentMemberDialog from './AlertDifferentMemberDialog';
import AlertDifferentAccountDialog from './AlertDifferentAccountDialog';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  quickButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  quickButton: {
    marginLeft: 3,
    marginRight: 3,
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
}));

export interface Values {
  email: string;
  firstName: string;
  lastName: string;
  dob: moment.Moment | null;
  mobNum: string;
  countryCode: string;
  notes: string;
}

const ValidationSchema = Yup.object().shape(
  {
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string()
      .email()
      .when('mobNum', {
        is: (mobNum: string) => !mobNum || mobNum.length === 0,
        then: Yup.string()
          .email()
          .required('Mobile Number or Email is required'),
        otherwise: Yup.string(),
      }),
    mobNum: Yup.string().when('email', {
      is: (email: string) => !email || email.length === 0,
      then: Yup.string().required('Email or Mobile Number is required'),
      otherwise: Yup.string(),
    }),
  },
  [['email', 'mobNum']]
);

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: (memberId: string) => void;
};

export default function NewMemberDialog({ open, onClose, onSuccess }: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const [showQuickButton, setShowQuickButton] = useState(false);
  const [showAlertDifferentMemberDialog, setShowAlertDifferentMemberDialog] =
    useState(false);
  const [
    showAlertExistingMemberEmailDialog,
    setShowAlertExistingMemberEmailDialog,
  ] = useState(false);
  const [
    showAlertExistingMemberMobileDialog,
    setShowAlertExistingMemberMobileDialog,
  ] = useState(false);
  const [showAlertDifferentAccountDialog, setShowAlertDifferentAccountDialog] =
    useState(false);
  const [memberList, setMemberList] = useState<
    {
      id: string;
      name: string;
      email: string;
      mobileNum: string;
    }[]
  >([]);
  const [unmatchUser, setUnmatchUser] = useState<
    | {
        id: string;
        name: string;
        email: string;
        mobileNum: string;
      }
    | undefined
  >(undefined);
  const [formInfo, setFormInfo] = useState<Values | undefined>(undefined);
  const [unmatchType, setUnmatchType] = useState('');

  const initialValues: Values = {
    email: '',
    firstName: '',
    lastName: '',
    dob: null,
    mobNum: '',
    notes: '',
    countryCode:
      company?.country === 'sg'
        ? 'SG'
        : company?.country === 'us'
        ? 'US'
        : company?.country === 'ca'
        ? 'CA'
        : company?.country === 'au'
        ? 'AU'
        : company?.country === 'nz'
        ? 'NZ'
        : company?.country === 'gb'
        ? 'GB'
        : '',
  };

  const updateMemberNotes = async (note: string, memberId: string) => {
    if (company) {
      try {
        await MemberService.updateNotes({
          companyId: company.id,
          notes: note,
          memberID: memberId,
        });
      } catch (e) {
        console.log("Can't update member notes: ", e);
      }
    }
  };

  const updateMemberDOB = async (dob: string, userId: string) => {
    if (company) {
      try {
        await UserService.updateUserDOB({
          companyId: company.id,
          dob: dob,
          userID: userId,
        });
      } catch (e) {
        console.log("Can't update member notes: ", e);
      }
    }
  };

  const checkUserEmailExist = async (userEmail: string) => {
    // match userEmail with memberList
    if (memberList.length > 0 && company && formInfo) {
      const matchUserEmail = find(memberList, function (member) {
        return member.email === userEmail.toLowerCase();
      });

      const callingCode = find(codes, function (code) {
        return code.isoCode2 === formInfo.countryCode;
      });

      if (!matchUserEmail) {
        // create user

        if (formInfo.mobNum.length > 0 && callingCode) {
          const resMember = await MemberService.createUserWithMobileAndEmail({
            firstName: formInfo.firstName,
            lastName: formInfo.lastName,
            email: userEmail.toLowerCase(),
            mobNum:
              '+' + callingCode.countryCodes + ' ' + formInfo.mobNum.trim(),
            companyId: company.id,
          });

          if (resMember[0].user.id && formInfo.dob) {
            const dobStr = formInfo.dob
              ? `${formInfo.dob?.year()}-${(formInfo.dob?.month() + 1)
                  .toString()
                  .padStart(2, '0')}-${formInfo.dob
                  ?.date()
                  .toString()
                  .padStart(2, '0')}`
              : '';
            await updateMemberDOB(dobStr, resMember[0].user.id);
          }

          if (resMember.length > 0 && formInfo && formInfo.notes.length > 0) {
            await updateMemberNotes(formInfo.notes, resMember[0].id);
            onSuccess(resMember[0].id);
          } else {
            onSuccess(resMember[0].id);
          }
        }
      } else {
        // check if matchUserEmail mobile is null
        if (!matchUserEmail.mobileNum && formInfo && callingCode) {
          // update user profile
          await UserService.updateUserMobile({
            companyId: company.id,
            mobNum:
              '+' + callingCode.countryCodes + ' ' + formInfo.mobNum.trim(),
            userID: matchUserEmail.id,
          });
          // check if user is a member
          setUserAsMember(matchUserEmail.id);
        }
        // if matchUserEmail mobile is not null
        if (matchUserEmail.mobileNum) {
          // check if mobile num match with matchUserEmail
          if (formInfo) {
            if (formInfo.mobNum === matchUserEmail.mobileNum) {
              // check if matchUserEmail is an existing user
              // check if user is a member
              setUserAsMember(matchUserEmail.id);
            } else {
              // launch Alert dialog with
              setUnmatchType('mobile');
              setUnmatchUser(matchUserEmail);
              setShowAlertDifferentAccountDialog(true);
            }
          }
        }
      }
    }
  };

  const checkUserMobileExist = async (userMobile: string) => {
    // match userMobile with memberList
    if (memberList.length > 0 && company) {
      const matchUserMobile = find(memberList, function (member) {
        return member.mobileNum === userMobile;
      });
      if (!matchUserMobile) {
        // create user
        if (formInfo) {
          if (userMobile.length > 0) {
            const resMember = await MemberService.createUserWithMobileAndEmail({
              firstName: formInfo.firstName,
              lastName: formInfo.lastName,
              email: formInfo.email.toLowerCase(),
              mobNum: userMobile,
              companyId: company.id,
            });

            if (resMember[0].user.id && formInfo.dob) {
              const dobStr = formInfo.dob
                ? `${formInfo.dob?.year()}-${(formInfo.dob?.month() + 1)
                    .toString()
                    .padStart(2, '0')}-${formInfo.dob
                    ?.date()
                    .toString()
                    .padStart(2, '0')}`
                : '';
              await updateMemberDOB(dobStr, resMember[0].user.id);
            }

            if (resMember.length > 0 && formInfo && formInfo.notes.length > 0) {
              await updateMemberNotes(formInfo.notes, resMember[0].id);
              onSuccess(resMember[0].id);
            } else {
              onSuccess(resMember[0].id);
            }
          }
        }
      } else {
        // check if matchUserMobile email is null
        if (!matchUserMobile.email && formInfo) {
          // update user profile
          await UserService.updateUserEmail({
            companyId: company.id,
            email: formInfo.email.toLowerCase(),
            userID: matchUserMobile.id,
          });
          // check if user is a member
          setUserAsMember(matchUserMobile.id);
        }

        // if matchUserMobile email is not null
        if (matchUserMobile.email) {
          // check if email match with matchUserMobile
          if (formInfo) {
            if (formInfo.email.toLowerCase() === matchUserMobile.email) {
              // check if matchUserEmail is an existing user
              // check if user is a member
              setUserAsMember(matchUserMobile.id);
            } else {
              // launch Alert dialog with
              setUnmatchType('email');
              setUnmatchUser(matchUserMobile);
              setShowAlertDifferentAccountDialog(true);
            }
          }
        }
      }
    }
  };

  const setUserAsMember = async (userId: string) => {
    try {
      if (company) {
        try {
          const isExistingMember = await MemberService.getMemberByUserID(
            company.id,
            userId
          );

          if (isExistingMember) {
            if (formInfo && formInfo.notes.length > 0) {
              if (userId && formInfo.dob) {
                const dobStr = formInfo.dob
                  ? `${formInfo.dob?.year()}-${(formInfo.dob?.month() + 1)
                      .toString()
                      .padStart(2, '0')}-${formInfo.dob
                      ?.date()
                      .toString()
                      .padStart(2, '0')}`
                  : '';
                await updateMemberDOB(dobStr, userId);
              }
              await updateMemberNotes(formInfo.notes, isExistingMember.id);
              onSuccess(isExistingMember.id);
            } else {
              onSuccess(isExistingMember.id);
            }
          }
        } catch (e) {
          const resAddMember = await MemberService.create({
            companyId: company.id,
            userID: userId,
          });

          if (resAddMember.length > 0) {
            if (formInfo && formInfo.notes.length > 0) {
              if (userId && formInfo.dob) {
                const dobStr = formInfo.dob
                  ? `${formInfo.dob?.year()}-${(formInfo.dob?.month() + 1)
                      .toString()
                      .padStart(2, '0')}-${formInfo.dob
                      ?.date()
                      .toString()
                      .padStart(2, '0')}`
                  : '';
                await updateMemberDOB(dobStr, userId);
              }
              await updateMemberNotes(formInfo.notes, resAddMember[0].id);
              onSuccess(resAddMember[0].id);
            } else {
              onSuccess(resAddMember[0].id);
            }
          }
        }
      }
    } catch (e) {
      console.log('Error: ', e);
    }
  };

  const handleSubmit = async (values: Values, _: FormikHelpers<Values>) => {
    // const dobStr = values.dob
    //   ? `${values.dob?.year()}-${(values.dob?.month() + 1)
    //       .toString()
    //       .padStart(2, '0')}-${values.dob?.date().toString().padStart(2, '0')}`
    //   : '';
    setFormInfo(values);
    const callingCode = find(codes, function (code) {
      return code.isoCode2 === values.countryCode;
    });
    if (company && callingCode) {
      // user provide only email, FN and LN
      if (values.email.length > 0 && values.mobNum.length === 0) {
        try {
          // check if email exist
          const resIsUserEmail = await UserService.findUser(
            company.id,
            values.email.toLowerCase(),
            ''
          );
          if (resIsUserEmail.id) {
            setUserAsMember(resIsUserEmail.id);
          }
        } catch (e) {
          // check if FN and LN match
          try {
            const resUserNameExist = await UserService.findUsers(
              company.id,
              values.firstName,
              values.lastName
            );

            if (resUserNameExist.length === 0) {
              // create user with email only
              if (values.email.length > 0 && values.mobNum.length === 0) {
                const resMember = await MemberService.createUserWithEmail({
                  firstName: values.firstName,
                  lastName: values.lastName,
                  email: values.email.toLowerCase(),
                  companyId: company.id,
                });

                if (resMember.length > 0) {
                  if (resMember[0].user.id && values.dob) {
                    const dobStr = values.dob
                      ? `${values.dob?.year()}-${(values.dob?.month() + 1)
                          .toString()
                          .padStart(2, '0')}-${values.dob
                          ?.date()
                          .toString()
                          .padStart(2, '0')}`
                      : '';
                    await updateMemberDOB(dobStr, resMember[0].user.id);
                  }
                  if (values.notes.length > 0) {
                    await updateMemberNotes(values.notes, resMember[0].id);
                    onSuccess(resMember[0].id);
                  } else {
                    onSuccess(resMember[0].id);
                  }
                }
              }
            }

            if (resUserNameExist.length > 0) {
              let mapList = resUserNameExist.map((user) => {
                return {
                  id: user.id,
                  name: user.firstName + ' ' + user.lastName,
                  email: user.email.toLowerCase(),
                  mobileNum: user.mobNum,
                };
              });

              setMemberList(mapList);
              setShowAlertExistingMemberMobileDialog(true);
            }
          } catch (e) {
            console.log('Error no name match found');
            // create user with email only
            if (values.email.length > 0 && values.mobNum.length === 0) {
              const resMember = await MemberService.createUserWithEmail({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email.toLowerCase(),
                companyId: company.id,
              });

              if (resMember.length > 0) {
                if (resMember[0].id && values.dob) {
                  const dobStr = values.dob
                    ? `${values.dob?.year()}-${(values.dob?.month() + 1)
                        .toString()
                        .padStart(2, '0')}-${values.dob
                        ?.date()
                        .toString()
                        .padStart(2, '0')}`
                    : '';
                  await updateMemberDOB(dobStr, resMember[0].user.id);
                }
                if (values.notes.length > 0) {
                  await updateMemberNotes(values.notes, resMember[0].id);
                  onSuccess(resMember[0].id);
                } else {
                  onSuccess(resMember[0].id);
                }
              }
            }
          }
        }
      }

      // user provide only mobile, FN and LN
      if (values.email.length === 0 && values.mobNum.length > 0) {
        try {
          // check if email exist
          const resIsUserEmail = await UserService.findUser(
            company.id,
            '',
            '+' + callingCode.countryCodes + ' ' + values.mobNum.trim()
          );
          if (resIsUserEmail.id) {
            setUserAsMember(resIsUserEmail.id);
          }
        } catch (e) {
          // check if FN and LN match
          try {
            const resUserNameExist = await UserService.findUsers(
              company.id,
              values.firstName,
              values.lastName
            );

            if (resUserNameExist.length === 0) {
              // create user with mobNum only
              if (values.mobNum.length > 0) {
                const resMember = await MemberService.createUserWithMobile({
                  firstName: values.firstName,
                  lastName: values.lastName,
                  mobNum:
                    values.mobNum.length > 0
                      ? '+' +
                        callingCode.countryCodes +
                        ' ' +
                        values.mobNum.trim()
                      : '',
                  companyId: company.id,
                });

                if (resMember.length > 0) {
                  if (resMember[0].user.id && values.dob) {
                    const dobStr = values.dob
                      ? `${values.dob?.year()}-${(values.dob?.month() + 1)
                          .toString()
                          .padStart(2, '0')}-${values.dob
                          ?.date()
                          .toString()
                          .padStart(2, '0')}`
                      : '';
                    await updateMemberDOB(dobStr, resMember[0].user.id);
                  }
                  if (values.notes.length > 0) {
                    await updateMemberNotes(values.notes, resMember[0].id);
                    onSuccess(resMember[0].id);
                  } else {
                    onSuccess(resMember[0].id);
                  }
                }
              }
            }

            if (resUserNameExist.length > 0) {
              let mapList = resUserNameExist.map((user) => {
                return {
                  id: user.id,
                  name: user.firstName + ' ' + user.lastName,
                  email: user.email,
                  mobileNum: user.mobNum,
                };
              });

              setMemberList(mapList);
              setShowAlertExistingMemberEmailDialog(true);
            }
          } catch (e) {
            // create user with mobNum only
            if (values.email.length === 0 && values.mobNum.length > 0) {
              const resMember = await MemberService.createUserWithMobile({
                firstName: values.firstName,
                lastName: values.lastName,
                mobNum:
                  values.mobNum.length > 0
                    ? '+' +
                      callingCode.countryCodes +
                      ' ' +
                      values.mobNum.trim()
                    : '',
                companyId: company.id,
              });

              if (resMember.length > 0) {
                if (resMember[0].user.id && values.dob) {
                  const dobStr = values.dob
                    ? `${values.dob?.year()}-${(values.dob?.month() + 1)
                        .toString()
                        .padStart(2, '0')}-${values.dob
                        ?.date()
                        .toString()
                        .padStart(2, '0')}`
                    : '';
                  await updateMemberDOB(dobStr, resMember[0].user.id);
                }
                if (values.notes.length > 0) {
                  await updateMemberNotes(values.notes, resMember[0].id);
                  onSuccess(resMember[0].id);
                } else {
                  onSuccess(resMember[0].id);
                }
              }
            }
          }
        }
      }

      // user provide only email and mobile, FN and LN
      if (values.email.length > 0 && values.mobNum.length > 0) {
        try {
          // find user with both email and mobile
          const resIsUser = await UserService.findUser(
            company.id,
            values.email.toLowerCase(),
            '+' + callingCode.countryCodes + ' ' + values.mobNum.trim()
          );
          setUserAsMember(resIsUser.id);
        } catch (e) {
          try {
            // find user with email
            const resIsUserEmail = await UserService.findUser(
              company.id,
              values.email.toLowerCase(),
              ''
            );

            try {
              // find user with mobile
              const resIsUserMobNum = await UserService.findUser(
                company.id,
                '',
                '+' + callingCode.countryCodes + ' ' + values.mobNum.trim()
              );

              if (resIsUserEmail && resIsUserMobNum) {
                if (
                  resIsUserEmail.firstName !== values.firstName ||
                  resIsUserEmail.lastName !== values.lastName ||
                  resIsUserMobNum.firstName !== values.firstName ||
                  resIsUserMobNum.lastName !== values.lastName
                ) {
                  let mapList = [];
                  mapList.push({
                    id: resIsUserEmail.id,
                    name:
                      resIsUserEmail.firstName + ' ' + resIsUserEmail.lastName,
                    email: resIsUserEmail.email,
                    mobileNum: resIsUserEmail.mobNum,
                  });
                  mapList.push({
                    id: resIsUserMobNum.id,
                    name:
                      resIsUserMobNum.firstName +
                      ' ' +
                      resIsUserMobNum.lastName,
                    email: resIsUserMobNum.email,
                    mobileNum: resIsUserMobNum.mobNum,
                  });
                  setMemberList(mapList);
                  setShowAlertDifferentMemberDialog(true);
                }
              }
            } catch (e) {
              // no user with mobile
              if (
                resIsUserEmail.mobNum.length === 0 ||
                !resIsUserEmail.mobNum
              ) {
                // update user mobile
                await UserService.updateUserMobile({
                  companyId: company.id,
                  mobNum:
                    '+' + callingCode.countryCodes + ' ' + values.mobNum.trim(),
                  userID: resIsUserEmail.id,
                });
              }
              // set user as member
              setUserAsMember(resIsUserEmail.id);
            }
          } catch (e) {
            // if no user with email, check if there is user with mobile
            try {
              // find user with mobile
              const resIsUserMobNum = await UserService.findUser(
                company.id,
                '',
                '+' + callingCode.countryCodes + ' ' + values.mobNum.trim()
              );
              if (resIsUserMobNum) {
                if (resIsUserMobNum.email.length === 0) {
                  // update user email
                  await UserService.updateUserEmail({
                    companyId: company.id,
                    email: values.email.toLowerCase(),
                    userID: resIsUserMobNum.id,
                  });
                }
                // set user as member
                setUserAsMember(resIsUserMobNum.id);
              }
            } catch (e) {
              // No existing user email and mobile number
              // no user with mobile and email, create new user
              const resMember =
                await MemberService.createUserWithMobileAndEmail({
                  firstName: values.firstName,
                  lastName: values.lastName,
                  email: values.email.toLowerCase(),
                  mobNum:
                    '+' + callingCode.countryCodes + ' ' + values.mobNum.trim(),
                  companyId: company.id,
                });

              if (resMember.length > 0) {
                if (resMember[0].user.id && values.dob) {
                  const dobStr = values.dob
                    ? `${values.dob?.year()}-${(values.dob?.month() + 1)
                        .toString()
                        .padStart(2, '0')}-${values.dob
                        ?.date()
                        .toString()
                        .padStart(2, '0')}`
                    : '';
                  await updateMemberDOB(dobStr, resMember[0].user.id);
                }
                if (values && values.notes.length > 0) {
                  await updateMemberNotes(values.notes, resMember[0].id);
                  onSuccess(resMember[0].id);
                } else {
                  onSuccess(resMember[0].id);
                }
              }
            }
          }
        }
      }
    }
  };

  // TODO - Display a loader
  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, values: { email }, setFieldValue }) => (
          <Form noValidate>
            <AlertDifferentMemberDialog
              open={showAlertDifferentMemberDialog}
              onClose={() => setShowAlertDifferentMemberDialog(false)}
              onCancel={() => setShowAlertDifferentMemberDialog(false)}
              onConfirm={(userId: string) => {
                // set user to be member
                setUserAsMember(userId);
                setShowAlertDifferentMemberDialog(false);
              }}
              title="Alert"
              contentText="Customer might have different accounts with Flex Rewards. Please ask customer which account they wish to use. Cancel to start again."
              confirmButtonText="CONTINUE"
              cancelButtonText="CANCEL"
              memberList={memberList}
            />
            <AlertExistingMemberEmailDialog
              open={showAlertExistingMemberEmailDialog}
              onClose={() => setShowAlertExistingMemberEmailDialog(false)}
              onCancel={() => setShowAlertExistingMemberEmailDialog(false)}
              onConfirm={(email: string) => {
                // check email from user
                checkUserEmailExist(email);
                setShowAlertExistingMemberEmailDialog(false);
              }}
              title="Alert"
              contentText="Customer might have an existing account with Flex Rewards. Please ask customer for their email address."
              confirmButtonText="CONTINUE"
            />
            <AlertExistingMemberMobileDialog
              intialCountryCode={initialValues.countryCode}
              open={showAlertExistingMemberMobileDialog}
              onClose={() => setShowAlertExistingMemberMobileDialog(false)}
              onCancel={() => setShowAlertExistingMemberMobileDialog(false)}
              onConfirm={(mobileNum: string) => {
                // check mobile number from user
                checkUserMobileExist(mobileNum);
                setShowAlertExistingMemberMobileDialog(false);
              }}
              title="Alert"
              contentText="Customer might have an existing account with Flex Rewards. Please ask customer for their mobile number."
              confirmButtonText="CONTINUE"
            />
            {unmatchUser && (
              <AlertDifferentAccountDialog
                open={showAlertDifferentAccountDialog}
                onClose={() => setShowAlertDifferentAccountDialog(false)}
                onCancel={() => setShowAlertDifferentAccountDialog(false)}
                onConfirm={(userId: string) => {
                  // set user as member
                  setUserAsMember(userId);
                  setShowAlertDifferentAccountDialog(false);
                }}
                title="Alert"
                contentText={`Customer has an existing account with a different ${
                  unmatchType === 'mobile'
                    ? unmatchType + ' number'
                    : unmatchType
                }. Customer will be assigned to that account.`}
                confirmButtonText="CONTINUE"
                cancelButtonText="CANCEL"
                unmatchUser={unmatchUser}
              />
            )}
            <div className={classes.box}>
              <IconButton onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.content}>
                  <Container maxWidth="md">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div className={classes.actionContainer}>
                          <Typography variant="h4">Add Member</Typography>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            DONE
                          </Button>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          name="firstName"
                          label="First Name (required)"
                          fullWidth
                          onFocus={() => setShowQuickButton(false)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          name="lastName"
                          label="Last Name (required)"
                          fullWidth
                          onFocus={() => setShowQuickButton(false)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          inputProps={{ style: { textTransform: 'lowercase' } }}
                          name="email"
                          label="Email Address"
                          fullWidth
                          onFocus={() => setShowQuickButton(true)}
                        />
                      </Grid>
                      {showQuickButton && (
                        <Grid item xs={12}>
                          <div className={classes.quickButtonContainer}>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@gmail.com')
                              }
                            >
                              @gmail.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@yahoo.com')
                              }
                            >
                              @yahoo.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@hotmail.com')
                              }
                            >
                              @hotmail.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@me.com')
                              }
                            >
                              @me.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@aol.com')
                              }
                            >
                              @aol.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@ymail.com')
                              }
                            >
                              @ymail.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@msn.com')
                              }
                            >
                              @msn.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@outlook.com')
                              }
                            >
                              @outlook.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@icloud.com')
                              }
                            >
                              @icloud.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@live.com')
                              }
                            >
                              @live.com
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '@')
                              }
                            >
                              @
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '.')
                              }
                            >
                              .
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '_')
                              }
                            >
                              _
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '-')
                              }
                            >
                              -
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '+')
                              }
                            >
                              +
                            </Button>
                            <Button
                              className={classes.quickButton}
                              onClick={() =>
                                setFieldValue('email', email + '.com')
                              }
                            >
                              .com
                            </Button>
                          </div>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <InputLabel>Mobile Number</InputLabel>
                      </Grid>
                      <Grid item xs={5}>
                        <Field
                          component={Select}
                          name="countryCode"
                          label=""
                          fullWidth
                          onFocus={() => setShowQuickButton(false)}
                        >
                          {codes.map((code) => (
                            <MenuItem value={code.isoCode2} key={code.isoCode2}>
                              {'+' + code.countryCodes + ' ' + code.country}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={7}>
                        <Field
                          component={TextField}
                          name="mobNum"
                          label=""
                          fullWidth
                          onFocus={() => setShowQuickButton(false)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={DatePicker}
                          name="dob"
                          label="Date of Birth"
                          format="D MMM YYYY"
                          fullWidth
                          onFocus={() => setShowQuickButton(false)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          name="notes"
                          label="Notes"
                          multiline
                          minRows={3}
                          maxRows={6}
                          fullWidth
                          onFocus={() => setShowQuickButton(false)}
                        />
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
